import React, {useContext, useEffect, useRef, useState} from 'react';
import {Handle, Position, useUpdateNodeInternals} from '@xyflow/react';
import {LineageContext} from './LineageContext';
import {nodeColor} from '../util/theme';
import {elKey} from '../util/graphTraversal';
import {resolveIcon} from '../util/characteristicIcons';
import {Public as PublicIcon} from '@material-ui/icons';

const HALF = 250;

/**
 * The flippable application/system node.
 * Front face  = system-lineage details
 * Back face   = data-element rows with handles
 * Added: characteristic icon badge (top-left), SRC/DEST chip (top-right),
 * CDE marker on element rows in back face.
 */
const AppNode = ({id, data, selected}) => {
  const {lineageViewType, viewMode, theme, actions, characteristics, ddColumns} = useContext(LineageContext);
  const updateNodeInternals = useUpdateNodeInternals();
  const dataView = lineageViewType === 'data';
  const targetFace = dataView ? 'back' : 'front';

  const [face, setFace] = useState(targetFace);
  const [rot, setRot] = useState(0);
  const [animating, setAnimating] = useState(false);
  const timers = useRef([]);
  const faceRef = useRef(face);
  useEffect(() => {
    faceRef.current = face;
  }, [face]);

  useEffect(() => {
    if (faceRef.current === targetFace) return undefined;
    timers.current.forEach(clearTimeout);
    setAnimating(true);
    setRot(90);
    const t1 = setTimeout(() => {
      setFace(targetFace);
      setAnimating(false);
      setRot(-90);
      updateNodeInternals(id);
      const t2 = setTimeout(() => {
        setAnimating(true);
        setRot(0);
        const t3 = setTimeout(() => {
          setAnimating(false);
          updateNodeInternals(id);
        }, HALF);
        timers.current.push(t3);
      }, 16);
      timers.current.push(t2);
    }, HALF);
    timers.current = [t1];
    return () => timers.current.forEach(clearTimeout);
  }, [targetFace, id, updateNodeInternals]);

  const elementIds = dataView && Array.isArray(data.dataElements) ? data.dataElements.map((e) => e.id).join('|') : '';
  useEffect(() => {
    if (dataView) updateNodeInternals(id);
  }, [id, dataView, elementIds, updateNodeInternals]);

  const color = nodeColor(theme, data.appType);

  // Resolve characteristic for the badge
  const charList = characteristics || [];
  const characteristic = data.characteristicId ?
    charList.find((c) => c.id === data.characteristicId) :
    null;
  const charIcon = characteristic ? resolveIcon(characteristic.icon) : null;
  const charColor = (characteristic && characteristic.color) || '#1976d2';
  const charStale = data.characteristicId && !characteristic;

  const rootClass = [
    'ln-appnode',
    data.__derivedTable ? 'ln-derived-in' : '',
    selected ? 'ln-selected' : '',
    data.highlighted ? 'ln-highlighted' : '',
    data.dimmed ? 'ln-dimmed' : '',
    data.isSource ? 'ln-source' : '',
  ].filter(Boolean).join(' ');

  const flipStyle = {
    transform: `perspective(900px) rotateY(${rot}deg)`,
    transition: animating ? `transform ${HALF}ms ease-in-out` : 'none',
  };

  return (
    <div className={rootClass} style={{'--ln-node-color': color, 'fontSize': theme.fontSize}} data-testid={`appnode-${id}`}>
      {/* Characteristic badge — top-left corner */}
      {(charIcon || charStale) && (
        <span
          className="ln-badge-char"
          title={charStale ? 'Characteristic no longer active' : (characteristic && characteristic.name)}
          data-testid={`char-badge-${id}`}
          style={{color: charStale ? '#9e9e9e' : charColor}}
        >
          {charStale ? '?' : React.createElement(charIcon, {style: {fontSize: 14}})}
        </span>
      )}

      {/* SRC/DEST chip — top-right corner */}
      {(data.isSource || data.isDestination) && (
        <span className="ln-chip-srcdest" data-testid={`srcdest-chip-${id}`}>
          {data.isSource && <span className="ln-chip ln-chip-src">SRC</span>}
          {data.isDestination && <span className="ln-chip ln-chip-dest">DEST</span>}
        </span>
      )}

      <Handle
        id="node-target" type="target" position={Position.Left}
        isConnectable={!dataView}
        className={`ln-handle ln-handle-node${dataView ? ' ln-handle-hidden' : ''}`}
        style={{background: theme.handle}}
      />
      <Handle
        id="node-source" type="source" position={Position.Right}
        isConnectable={!dataView}
        className={`ln-handle ln-handle-node${dataView ? ' ln-handle-hidden' : ''}`}
        style={{background: theme.handle}}
      />

      <div className="ln-flip" style={flipStyle}>
        {face === 'front' ? (
          <FrontFace id={id} data={data} color={color} editable={viewMode === 'edit'} actions={actions} />
        ) : (
          <BackFace
            id={id} data={data} theme={theme} viewMode={viewMode} actions={actions}
            isDestination={!!data.isDestination} ddColumns={ddColumns}
          />
        )}
      </div>
    </div>
  );
};

const FrontFace = ({id, data, color, editable, actions}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const isExternal = !!(data.details && data.details.isExternal);

  const startEdit = (e) => {
    if (!editable) return;
    e.stopPropagation();
    setDraft(data.label || '');
    setEditing(true);
  };
  const commit = () => {
    if (actions && actions.updateNode) actions.updateNode(id, {data: {label: draft.trim()}});
    setEditing(false);
  };

  // Count hint: a database that merged a view + its source tables shows "1 view · 2 tables";
  // otherwise the uniform "N table(s)" / "N child node(s)".
  const tables = Array.isArray(data.tables) ? data.tables : [];
  const dvMeta = (data.details && data.details.dvMeta) || {};
  const viewCount = tables.filter((t) => dvMeta[t.id] && dvMeta[t.id].role === 'view').length;
  const srcCount = tables.length - viewCount;
  let tableHint = null;
  if (tables.length > 0) {
    tableHint = data.appType === 'database' && viewCount > 0 && srcCount > 0 ?
      `${viewCount} view${viewCount !== 1 ? 's' : ''} · ${srcCount} table${srcCount !== 1 ? 's' : ''}` :
      `${tables.length} ${data.appType === 'database' ? 'table(s)' : 'child node(s)'}`;
  }

  return (
    <div className="ln-face ln-front" style={{borderTopColor: color}}>
      <div className="ln-node-header">
        <span className="ln-node-type-dot" style={{background: color}} />
        {editing ? (
          <input
            className="ln-node-title-input"
            value={draft}
            autoFocus
            data-testid={`node-label-input-${id}`}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                setEditing(false);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className="ln-node-title" data-testid={`node-label-${id}`}
            title={editable ? 'Double-click to rename' : data.label}
            onClick={(e) => editable && e.stopPropagation()}
            onDoubleClick={startEdit}
          >{data.label || 'Untitled'}</span>
        )}
        {isExternal && <PublicIcon titleAccess="External" data-testid={`external-icon-${id}`} style={{fontSize: 14, color: '#6b7684', flexShrink: 0}} />}
      </div>
      <div className="ln-node-appType">{data.appType || 'application'}</div>
      {data.description ? <div className="ln-node-desc" title={data.description}>{data.description}</div> : null}
      {data.details && data.details.environment ? (
        <div className="ln-node-detail">env: {data.details.environment}</div>
      ) : null}
      {tableHint ? (
        <div className="ln-node-elements-count">{tableHint}</div>
      ) : Array.isArray(data.dataElements) && data.dataElements.length > 0 ? (
        <div className="ln-node-elements-count">{data.dataElements.length} data element(s)</div>
      ) : null}
    </div>
  );
};

const BackFace = ({id, data, theme, viewMode, actions, isDestination, ddColumns}) => {
  const [draft, setDraft] = useState('');
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState('');
  // Inline rename on the card title — a child card renames its table, a flat node its label.
  // Database table cards are named by their DD object, so they aren't inline-renamed.
  const canRename = viewMode === 'edit' && data.appType !== 'database';
  const startRename = (e) => {
    if (!canRename) return;
    e.stopPropagation();
    setLabelDraft(data.label || '');
    setEditingLabel(true);
  };
  const commitRename = () => {
    if (actions && actions.renameCard) actions.renameCard(id, labelDraft.trim());
    setEditingLabel(false);
  };
  const elements = Array.isArray(data.dataElements) ? data.dataElements : [];
  const highlightedKeys = data.highlightedElementKeys;
  const keyNodeId = data.__dbNodeId || id;
  const keyTableId = data.__tableId || null;

  // DD-mapped table (objectId set) → elements are picked from the table's DD columns,
  // not free-typed. Fetch the columns and offer a dropdown of not-yet-added ones.
  const objectId = data.objectId || null;
  useEffect(() => {
    if (objectId && actions && actions.fetchDdColumns) actions.fetchDdColumns(objectId);
  }, [objectId, actions]);
  const cols = (objectId && ddColumns && ddColumns[objectId]) || [];
  const addedColIds = new Set(elements.map((el) => el.columnId).filter(Boolean));
  const availableCols = cols.filter((c) => !addedColIds.has(c.columnId || c.id));

  const addRow = () => {
    const name = draft.trim();
    if (!name) return;
    actions.addElement(id, {name, displayName: name});
    setDraft('');
  };

  const addColumn = (colId) => {
    const col = cols.find((c) => (c.columnId || c.id) === colId);
    if (!col) return;
    const name = col.name || col.columnName || '';
    actions.addElement(id, {name, displayName: name, columnId: col.columnId || col.id, dataType: col.dataType || col.type || ''});
  };

  const isExternal = !!(data.details && data.details.isExternal);
  return (
    <div className="ln-face ln-back" style={{borderTopColor: nodeColor(theme, data.appType)}}>
      <div className="ln-node-header ln-back-header">
        {editingLabel ? (
          <input
            className="ln-node-title-input" value={labelDraft} autoFocus
            data-testid={`card-label-input-${id}`}
            onChange={(e) => setLabelDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitRename();
              } else if (e.key === 'Escape') {
                setEditingLabel(false);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className="ln-node-title"
            title={canRename ? 'Double-click to rename' : data.label}
            data-testid={`card-label-${id}`}
            onClick={(e) => canRename && e.stopPropagation()}
            onDoubleClick={startRename}
          >{data.label || 'Untitled'}</span>
        )}
        {isExternal && <PublicIcon titleAccess="External" data-testid={`external-icon-${id}`} style={{fontSize: 14, color: '#6b7684', flexShrink: 0}} />}
      </div>
      <div className="ln-element-rows">
        {elements.length === 0 ? (
          <div className="ln-element-empty">No data elements</div>
        ) : elements.map((el) => {
          const key = elKey(keyNodeId, el.id, keyTableId);
          const highlighted = highlightedKeys && highlightedKeys.has(key);
          const dimmed = highlightedKeys && highlightedKeys.size > 0 && !highlighted;
          return (
            <div
              key={el.id}
              className={[
                'ln-element-row',
                highlighted ? 'ln-el-highlighted' : '',
                dimmed ? 'ln-el-dimmed' : '',
                el.isCde && isDestination ? 'ln-el-cde' : '',
              ].filter(Boolean).join(' ')}
              data-testid={`element-${id}-${el.id}`}
              onClick={(e) => {
                e.stopPropagation();
                actions.selectElement(id, el.id);
              }}
            >
              <Handle id={`el-${el.id}-target`} type="target" position={Position.Left} className="ln-handle ln-handle-el" style={{background: theme.handle}} />
              {/* display-anchor-only source handle on the left (for edges whose source sits to the right) */}
              <Handle id={`el-${el.id}-source-l`} type="source" position={Position.Left} isConnectable={false} className="ln-handle ln-handle-el ln-handle-hidden" />
              <span className="ln-el-name" title={el.description || el.name}>{el.displayName || el.name}</span>
              {el.dataType ? <span className="ln-el-type">{el.dataType}</span> : null}
              {el.isCde && isDestination ? (
                <span className="ln-el-cde-badge" title="Critical Data Element" data-testid={`cde-badge-${el.id}`}>CDE</span>
              ) : null}
              <Handle id={`el-${el.id}-source`} type="source" position={Position.Right} className="ln-handle ln-handle-el" style={{background: theme.handle}} />
              {/* display-anchor-only target handle on the right (for edges whose source sits to the right) */}
              <Handle id={`el-${el.id}-target-r`} type="target" position={Position.Right} isConnectable={false} className="ln-handle ln-handle-el ln-handle-hidden" />
            </div>
          );
        })}
      </div>
      {viewMode === 'edit' ? (
        objectId ? (
          <div className="ln-add-row" onClick={(e) => e.stopPropagation()}>
            <select
              className="ln-add-input"
              value=""
              data-testid={`add-column-${id}`}
              onChange={(e) => {
                if (e.target.value) addColumn(e.target.value);
              }}
            >
              <option value="">{availableCols.length ? 'Add column…' : 'All columns added'}</option>
              {availableCols.map((c) => {
                const cid = c.columnId || c.id;
                const nm = c.name || c.columnName || cid;
                return <option key={cid} value={cid}>{nm}{c.dataType ? ` (${c.dataType})` : ''}</option>;
              })}
            </select>
          </div>
        ) : (
          <div className="ln-add-row" onClick={(e) => e.stopPropagation()}>
            <input
              className="ln-add-input"
              value={draft}
              placeholder="Add data element…"
              data-testid={`add-element-${id}`}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addRow();
              }}
            />
            <button type="button" className="ln-add-btn" onClick={addRow}>+</button>
          </div>
        )
      ) : null}
    </div>
  );
};

export default React.memo(AppNode);
